import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowLeft, Palmtree } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setEmailSent(true);
      toast.success('Email envoyé !');
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error('Erreur lors de la demande');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-warm-sand flex items-center justify-center py-12 px-6">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Palmtree className="w-10 h-10 text-[#005F73]" strokeWidth={1.5} />
            <span className="text-2xl font-semibold text-[#005F73]">GwadImmo Invest</span>
          </div>
          <h1 className="text-3xl font-semibold text-[#005F73] mb-2">Mot de passe oublié</h1>
          <p className="text-stone-600">Entrez votre email pour réinitialiser votre mot de passe</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl border border-stone-100 p-8 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]">
          {!emailSent ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-3 w-5 h-5 text-stone-400" strokeWidth={1.5} />
                  <Input
                    data-testid="input-email"
                    id="email"
                    type="email"
                    required
                    placeholder="vous@exemple.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 rounded-xl"
                  />
                </div>
              </div>

              <Button
                data-testid="btn-submit"
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl bg-[#005F73] hover:bg-[#005F73]/90 text-lg font-medium"
              >
                {loading ? 'Envoi...' : 'Envoyer le lien'}
              </Button>
            </form>
          ) : (
            <div className="space-y-4" data-testid="email-sent-confirmation">
              <div className="bg-[#0A9396]/10 border border-[#0A9396] rounded-xl p-6 text-center">
                <Mail className="w-12 h-12 text-[#0A9396] mx-auto mb-3" strokeWidth={1.5} />
                <h3 className="text-lg font-semibold text-[#005F73] mb-2">Email envoyé !</h3>
                <p className="text-sm text-stone-600 leading-relaxed">
                  Si un compte existe avec l'adresse <strong>{email}</strong>, 
                  vous recevrez un email avec un lien pour réinitialiser votre mot de passe.
                </p>
              </div>
              
              <div className="text-xs text-stone-500 bg-stone-50 rounded-lg p-3">
                Pensez à vérifier vos spams si vous ne voyez pas l'email dans votre boîte de réception.
              </div>
            </div>
          )}

          <div className="mt-6 text-center text-sm text-stone-600">
            Vous vous souvenez de votre mot de passe ?{' '}
            <Link to="/login" className="text-[#005F73] font-medium hover:underline">
              Se connecter
            </Link>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-6 text-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-stone-600 hover:text-[#005F73]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Retour à l'accueil
          </Button>
        </div>
      </div>
    </div>
  );
}
